import React, { FC, useEffect, useState } from "react";
import { useParams } from "@Libs/Router/hooks/useParams";
import { IAppRoute, IExternalLink } from "@App/types";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";
import {
  PaymentMethodProviderCombination,
  PaymentMethodType,
} from "@modules/PaymentProcessing/types";
import { ConfirmationModal } from "@modules/Purchasing/Components/ConfirmationModal/ConfirmationModal";
import { Card } from "@UI/Components/Card/Card";
import { useService } from "@modules/ServiceProcessing/hooks/useService";
import { OrderPaid } from "@modules/Purchasing/Components/OrderDescription/OrderPaid/OrderPaid";
import { LatePaymentBankTransfer } from "@modules/ServiceProcessing/Components/LatePaymentBankTransfer";
import { ServiceProcessingEventStep } from "@modules/ServiceProcessing/types/serviceProcessing.types";
import { useRecordEvent } from "@Libs/Events/hooks/useRecordEvent";
import { useOrderPreview } from "@modules/Purchasing/hooks/useOrderPreview";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useOrderDescription } from "@modules/Purchasing/Components/OrderDescription/useOrderDescription";
import { useSearchParams } from "@Libs/Router/hooks/useSearchParams";

export const CLOSURE_BASE_ROUTE: IAppRoute[] = [
  "SERVICE_OFFERS",
  "SERVICE_OFFER",
  "CLOSE",
];

interface Props {
  basketId: string;
}

export const ServiceOfferClosureCheckoutFlow: FC<Props> = ({ basketId }) => {
  const { serviceOfferId } = useParams(CLOSURE_BASE_ROUTE);
  const isPaymentProcessing = useSearchParams("isPaymentProcessing") == "true";
  const navigate = useNavigate();

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successResponse, setSuccessResponse] = useState<
    | (PaymentMethodProviderCombination<PaymentMethodType> & {
        isPaymentConfirmed: boolean;
      })
    | undefined
  >(undefined);

  const { service } = useService(serviceOfferId);

  const { recordEvent: recordStepVisitedEvent } = useRecordEvent(
    {
      key: "contract_closure_flow.step_visited",
    },
    { triggerOnMount: false }
  );

  const {
    orderPreview,
    isAlreadyPaid,
    isAlreadyProcessed,
    isLoading: isOrderLoading,
  } = useOrderPreview({
    basketId,
  });

  const { totalServiceFees, totalExpensesFees } =
    useOrderDescription(orderPreview);

  useEffect(() => {
    if (
      service?.oppositionState === "fully_blocked" ||
      service?.oppositionState === "partially_blocked"
    ) {
      navigate("HOME", { reload: true });
    }
  }, [service?.oppositionState, service?.legacyPayment]);

  const handleCheckoutSuccess: (
    props: PaymentMethodProviderCombination<PaymentMethodType> & {
      isPaymentConfirmed: boolean;
    }
  ) => void = async (props) => {
    setSuccessResponse(props);
    setIsSuccessModalOpen(true);
  };

  if (!service || isOrderLoading) {
    return <LoadingScreen />;
  }

  if (
    service.legacyPayment?.origin === "credit" ||
    (isAlreadyPaid && !isPaymentProcessing)
  ) {
    return (
      <Card>
        <OrderPaid />
      </Card>
    );
  }

  if (
    isAlreadyProcessed &&
    service.legacyPayment?.origin === "bank_transfer" &&
    service.legacyPayment?.paid === false
  ) {
    return (
      <LatePaymentBankTransfer
        amountCents={service?.legacyPayment?.paidAmountCents}
        currency="EUR"
      />
    );
  }

  const handlePageMount = (route: keyof typeof ServiceProcessingEventStep) => {
    if (route === "CHECK_ORDER" || route === "SUBSCRIBE") {
      recordStepVisitedEvent({
        data: {
          contractId: Number(serviceOfferId),
          step: ServiceProcessingEventStep[route],
          service_fee_amount: totalServiceFees + totalExpensesFees,
        },
      });
    }
  };

  return (
    <>
      <Checkout
        onPageMount={handlePageMount}
        rootRoute={{
          to: CLOSURE_BASE_ROUTE,
        }}
        onLeave={() =>
          navigate(document.referrer as IExternalLink, { reload: true })
        }
        onSuccess={handleCheckoutSuccess}
        backRoute={{
          to: CLOSURE_BASE_ROUTE,
          params: { serviceOfferId },
        }}
        state={{
          basketId,
          relatedServiceId: Number(serviceOfferId),
        }}
      />
      <ConfirmationModal
        isOpen={isSuccessModalOpen}
        handleConfirm={() => {
          navigate("NEW_RATING", {
            queryParams: { contract_id: serviceOfferId },
            reload: true,
          });
        }}
        paymentMethod={successResponse?.paymentMethod}
        serviceOfferId={serviceOfferId}
      />
    </>
  );
};
